<script setup>
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../../store/auth'
import { useToast } from 'primevue/usetoast'

const router = useRouter()
const authStore = useAuthStore()
const toast = useToast()

const email = ref('')
const password = ref('')
const errorMessage = ref('')
const loading = ref(false)

const emailTouched = ref(false)
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const isEmailValid = computed(() => !email.value || emailPattern.test(email.value))
const canSubmit = computed(() => email.value.trim() && password.value.trim() && isEmailValid.value && !loading.value)

const clearError = () => {
  errorMessage.value = ''
}

const handleLogin = async () => {
  emailTouched.value = true

  if (!email.value || !password.value) {
    errorMessage.value = 'Por favor ingrese correo y contraseña'
    return
  }

  if (!isEmailValid.value) {
    errorMessage.value = 'Ingrese un correo electrónico válido'
    return
  }

  loading.value = true
  
  errorMessage.value = ''
  
  try {
    const success = await authStore.login(email.value, password.value)
    
    if (success) {
      toast.add({ 
        severity: 'success', 
        summary: 'Inicio de sesión exitoso', 
        detail: `Bienvenido ${authStore.user.nombreCompleto}`, 
        life: 3000 
      })
      
      // Redirigir según el rol
      if (authStore.isAdmin) {
        router.push('/portal-transparencia/admin')
      } else {
        router.push('/portal-transparencia')
      }
    } else {
      errorMessage.value = authStore.error || 'Error al iniciar sesión'
      toast.add({ 
        severity: 'error', 
        summary: 'Error', 
        detail: errorMessage.value, 
        life: 5000 
      })
    }
  } catch (error) {
    errorMessage.value = error.message || 'Error al iniciar sesión'
    toast.add({ 
      severity: 'error', 
      summary: 'Error', 
      detail: errorMessage.value, 
      life: 5000 
    })
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <main class="login-page">
    <Toast />

    <svg
      class="login-background"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 1600 800"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <rect fill="#008300" width="1600" height="800" />
      <path
        fill="#17a34a"
        d="M478.4 581c3.2.8 6.4 1.7 9.5 2.5 196.2 52.5 388.7 133.5 593.5 176.6 174.2 36.6 349.5 29.2 518.6-10.2V0H0v574.9c52.3-17.6 106.5-27.7 161.1-30.9 107.3-6.6 214.6 10.2 317.3 37z"
      />
      <path
        fill="#45bd66"
        d="M181.8 259.4c98.2 6 191.9 35.2 281.3 72.1 2.8 1.1 5.5 2.3 8.3 3.4 171 71.6 342.7 158.5 531.3 207.7 198.8 51.8 403.4 40.8 597.3-14.8V0H0v283.2c59-19.6 120.6-27.5 181.8-23.8z"
      />
      <path
        fill="#8fd79b"
        d="M454.9 86.3C600.7 177 751.6 269.3 924.1 325c208.6 67.4 431.3 60.8 637.9-5.3 12.8-4.1 25.4-8.4 38.1-12.9V0H288.1c56 21.3 108.7 50.6 159.7 82 2.4 1.4 4.7 2.9 7.1 4.3z"
      />
      <path
        fill="#dff4df"
        d="M1397.5 154.8c47.2-10.6 93.6-25.3 138.6-43.8 21.7-8.9 43-18.8 63.9-29.5V0H643.4c62.9 41.7 129.7 78.2 202.1 107.4 174.9 70.7 368.7 88.7 552 47.4z"
      />
    </svg>

    <section class="login-shell" aria-labelledby="login-title">
      <div class="login-card">
        <router-link to="/portal-transparencia" class="back-link">
          <i class="pi pi-arrow-left" aria-hidden="true"></i>
          Volver al portal
        </router-link>

        <div class="login-brand">
          <img src="/escudo1-200w.png" alt="Escudo institucional" class="login-logo" />
          <div>
            <p class="login-eyebrow">Portal de Transparencia</p>
            <h1 id="login-title" class="login-title">Ingreso administrativo</h1>
          </div>
        </div>

        <form class="login-form" @submit.prevent="handleLogin" novalidate>
          <div class="form-field">
            <label for="email">Correo electrónico</label>
            <span class="input-shell" :class="{ 'input-invalid': emailTouched && !isEmailValid }">
              <i class="pi pi-envelope" aria-hidden="true"></i>
              <InputText
                id="email"
                v-model="email"
                type="email"
                autocomplete="email"
                placeholder="usuario@colegio.edu.co"
                @blur="emailTouched = true"
                @input="clearError"
              />
            </span>
            <small v-if="emailTouched && !isEmailValid" class="field-error">
              El correo electrónico ingresado no es válido.
            </small>
          </div>

          <div class="form-field">
            <label for="password">Contraseña</label>
            <span class="input-shell">
              <i class="pi pi-lock" aria-hidden="true"></i>
              <InputText
                id="password"
                v-model="password"
                type="password"
                autocomplete="current-password"
                placeholder="Ingrese su contraseña"
                @input="clearError"
              />
            </span>
          </div>

          <Message v-if="errorMessage" severity="error" :closable="false" class="login-message">
            {{ errorMessage }}
          </Message>

          <Button
            type="submit"
            label="Iniciar sesión"
            icon="pi pi-sign-in"
            class="login-submit"
            :loading="loading"
            :disabled="!canSubmit"
          />
        </form>
      </div>
    </section>
  </main>
</template>

<style scoped>
.login-page {
  position: relative;
  min-height: 100vh;
  overflow: hidden;
  background: #f4f8f4;
}

.login-background {
  position: fixed;
  inset: 0;
  width: 100vw;
  min-width: 100vw;
  height: 100vh;
  min-height: 100vh;
}

.login-shell {
  position: relative;
  z-index: 1;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 32px 20px;
}

.login-card {
  width: 100%;
  max-width: 448px;
  padding: 32px;
  border: 1px solid rgba(11, 83, 31, 0.16);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 24px 70px rgba(6, 55, 22, 0.24);
  backdrop-filter: blur(8px);
}

.back-link {
  display: inline-flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 28px;
  color: #19642d;
  font-size: 0.92rem;
  font-weight: 700;
  text-decoration: none;
}

.back-link:hover {
  color: #0b3d1d;
}

.login-brand {
  display: flex;
  gap: 16px;
  align-items: center;
  margin-bottom: 30px;
}

.login-logo {
  width: 68px;
  height: 68px;
  object-fit: contain;
  flex: 0 0 auto;
}

.login-eyebrow {
  margin: 0 0 6px;
  color: #19642d;
  font-size: 0.82rem;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

.login-title {
  margin: 0;
  color: #102716;
  font-size: 1.8rem;
  line-height: 1.15;
  font-weight: 800;
  letter-spacing: 0;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-field label {
  color: #203629;
  font-size: 0.92rem;
  font-weight: 700;
}

.input-shell {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 48px;
  padding: 0 14px;
  border: 1px solid #c9d9cd;
  border-radius: 8px;
  background: #ffffff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.input-shell:focus-within {
  border-color: #008300;
  box-shadow: 0 0 0 3px rgba(0, 131, 0, 0.13);
}

.input-shell i {
  color: #2c7b3b;
  font-size: 1rem;
}

.input-shell :deep(.p-inputtext) {
  width: 100%;
  padding: 0;
  border: 0;
  color: #102716;
  background: transparent;
  box-shadow: none;
  outline: none;
}

.input-invalid {
  border-color: #dc3545;
}

.field-error {
  color: #b42318;
  font-weight: 600;
}

.login-message {
  margin: 0;
}

.login-submit {
  width: 100%;
  min-height: 48px;
  margin-top: 2px;
  border: 0;
  border-radius: 8px;
  background: #008300;
  font-weight: 800;
}

.login-submit:enabled:hover {
  background: #006f00;
}

@media (max-width: 520px) {
  .login-shell {
    align-items: stretch;
    padding: 20px 14px;
  }

  .login-card {
    align-self: center;
    padding: 24px 18px;
  }

  .login-brand {
    align-items: flex-start;
  }

  .login-logo {
    width: 56px;
    height: 56px;
  }

  .login-title {
    font-size: 1.45rem;
  }
}
</style>
